import { db, auth } from '../firebase/firebaseConfig';
import { doc, setDoc, getDoc, increment, serverTimestamp } from 'firebase/firestore';

const STORAGE_KEY = 'policytrackr_upload_counters';
const SYNC_INTERVAL = 60 * 60 * 1000; // 1 hour
let syncIntervalId = null;

// No time-based sync needed
/**
 * Get current IST month (YYYY-MM format)
 */
const getISTMonth = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(now.getTime() + istOffset);
  return istDate.toISOString().substring(0, 7); // Returns "YYYY-MM"
};

/**
 * Load counters from local storage
 * @returns {Object} Counters object
 */
const loadLocalCounters = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    console.error('Error loading local counters:', e);
    return {};
  }
};

/**
 * Save counters to local storage
 * @param {Object} counters - Counters to save
 */
const saveLocalCounters = (counters) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(counters));
  } catch (e) {
    console.error('Error saving local counters:', e);
  }
};

// No time-based sync needed

/**
 * Sync local counters with Firestore
 * @param {string} userId - The user's UID
 * @param {Object} counters - Local counters to sync
 */
const syncWithFirestore = async (userId, counters) => {
  if (!userId || !counters[userId] || counters[userId].pendingCount <= 0) {
    return false;
  }

  // Ensure user is authenticated and is syncing their own data
  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.uid !== userId) {
    // Silent fail - will try again when this user is authenticated
    return false;
  }

  const counterRef = doc(db, 'upload_counters', userId);
  const pendingCount = counters[userId].pendingCount;

  try {
    const currentMonth = getISTMonth();

    // Fetch the current counter to check for month reset
    const counterSnap = await getDoc(counterRef);
    const existingData = counterSnap.exists() ? counterSnap.data() : {};

    const isNewMonth = existingData.monthlyUploadMonth !== currentMonth;

    // Build update data
    const updateData = {
      userName: counters[userId].userName || existingData.userName || 'Unknown User',
      userEmail: counters[userId].userEmail || existingData.userEmail || '',
      totalUploadsLifetime: increment(pendingCount),
      // If it's a new month, archive current count and reset, otherwise increment
      prevMonthlyUploadCount: isNewMonth ? (existingData.monthlyUploadCount || 0) : (existingData.prevMonthlyUploadCount || 0),
      monthlyUploadCount: isNewMonth ? pendingCount : increment(pendingCount),
      monthlyUploadMonth: currentMonth,
      lastSynced: serverTimestamp(),
      lastUpdated: serverTimestamp(),
    };

    // Robust Name Fetching: Fetch agent's name if missing
    try {
      const agentDocRef = doc(db, 'agent-details', userId);
      const agentSnap = await getDoc(agentDocRef);
      if (agentSnap.exists()) {
        const agentData = agentSnap.data();
        updateData.userName = agentData.name || agentData.displayName || updateData.userName;
        updateData.userEmail = agentData.email || updateData.userEmail;
      }
    } catch (nameError) {
      console.warn('⚠️ uploadCounter: Failed to fetch agent name for sync:', nameError);
    }

    await setDoc(counterRef, updateData, { merge: true });

    // Clear pending count after successful sync
    counters[userId].pendingCount = 0;
    saveLocalCounters(counters);
    return true;
  } catch (error) {
    console.error('Error syncing with Firestore:', error);
    return false;
  }
};

/**
 * Check if we should sync based on the minimum threshold
 * @param {Object} counters - Current local counters
 * @returns {boolean} True if any user has reached the threshold
 */
const shouldSyncBasedOnThreshold = (counters) => {
  const MIN_UPLOADS_FOR_SYNC = 5;

  for (const userId in counters) {
    if (counters[userId].pendingCount >= MIN_UPLOADS_FOR_SYNC) {
      return true;
    }
  }

  return false;
};

// Check and sync all users
const syncAllUsers = async () => {
  const counters = loadLocalCounters();
  let hasChanges = false;

  for (const userId in counters) {
    if (counters[userId].pendingCount > 0) {
      const success = await syncWithFirestore(userId, counters);
      if (success) hasChanges = true;
    }
  }

  return hasChanges;
};

// Initialize the counter system with visibility change handling
export const initCounterSystem = () => {
  // Setup visibility change handler
  if (typeof document !== 'undefined') {
    let wasHidden = document.hidden;

    const handleVisibilityChange = () => {
      const isHidden = document.hidden;

      // If the page becomes visible after being hidden, sync if we have any pending counts
      if (wasHidden && !isHidden) {
        const counters = loadLocalCounters();
        // Check if we have any pending counts across all users
        const hasPendingCounts = Object.values(counters).some(
          counter => counter.pendingCount > 0
        );
        if (hasPendingCounts) {
          console.log('🔄 Page became visible with pending uploads - syncing...');
          syncAllUsers();
        }
      }

      wasHidden = isHidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }

  return () => { }; // Return empty cleanup function if not in browser
};

// Start the sync interval
export const startSyncInterval = () => {
  // Clear any existing interval
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
  }

  // Set up new interval
  syncIntervalId = setInterval(() => {
    syncAllUsers();
  }, SYNC_INTERVAL);

  // Initial sync
  syncAllUsers();

  // Return cleanup function
  return () => {
    if (syncIntervalId) {
      clearInterval(syncIntervalId);
      syncIntervalId = null;
    }
  };
};

// Initialize everything
export const initUploadCounter = () => {
  const cleanupVisibility = initCounterSystem();
  const cleanupInterval = startSyncInterval();

  // Return cleanup function
  return () => {
    cleanupVisibility();
    cleanupInterval();
  };
};

/**
 * Increments the upload counter for a specific user
 * @param {string} userId - The user's UID
 * @param {string} userName - The user's display name
 * @param {string} userEmail - The user's email
 * @param {Object} [user] - The user object containing additional info like agent details
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const incrementUploadCounter = async (userId, userName, userEmail, user = {}) => {
  if (!userId) {
    return {
      success: false,
      message: 'User ID is required'
    };
  }

  const counters = loadLocalCounters();

  // Initialize user counter if not exists
  if (!counters[userId]) {
    counters[userId] = {
      userName: 'Unknown User', // Will be updated below with the best available name
      userEmail: userEmail || '',
      pendingCount: 0,
      lastIncrement: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }

  // Update counter
  counters[userId].pendingCount += 1;
  counters[userId].lastIncrement = new Date().toISOString();
  counters[userId].updatedAt = new Date().toISOString();

  // Update user info if provided - prioritize agent name if available
  if (user?.agent?.name) {
    counters[userId].userName = user.agent.name;
  } else if (user?.agent?.displayName) {
    counters[userId].userName = user.agent.displayName;
  } else if (userName) {
    counters[userId].userName = userName;
  }

  if (userEmail) counters[userId].userEmail = userEmail;

  saveLocalCounters(counters);

  // If we've reached 5 uploads (threshold), sync immediately
  if (counters[userId].pendingCount >= 5) {
    await syncAllUsers();
  }

  return { success: true };
};
